import isSameDay from 'date-fns/isSameDay';
import _ from 'underscore';
import dropRightWhile from 'lodash.droprightwhile';

import * as ConversationActions from '../actionTypes/conversation';
import { RESET } from '../actionTypes/common';
import { replaceElement } from '../utils/arrays';
import { delegate } from '../utils/delegate';
import { SEND_STATUS, GLOBAL_ACTION_TYPES, MESSAGE_TYPES } from '../constants/message';
import { CONVERSATION_TYPES } from '../constants/conversations';
import { areFromSameAuthor } from '../utils/message';

const INITIAL_STATE = {
    default: {
        messages: [],
        rejectedMessages: {},
        completedUploads: [],
        replyActions: {
            actions: [],
            message: {},
        },
        isDefault: true, // true to enable channel linking on anonymous appUsers
        unreadCount: 0,
        hasMoreMessages: false,
        messagesUpdated: false,
        failedMessages: [],
        isFetchingMoreMessagesFromServer: false,
        lastReceivedMessageId: '',
        latestAppUserMessageTimestamp: 0,
        lastUpdatedAt: 0,
        participants: [],
        type: CONVERSATION_TYPES.PERSONAL,
        completedPrechatCapture: false,
    },
};

function formatConversation(conversation) {
    const {
        _id,
        unreadCount = 0,
        isDefault = false,
        type = CONVERSATION_TYPES.PERSONAL,
        messages = [],
        hasMoreMessages = false,
        latestAppUserMessageTimestamp = 0,
        lastReceivedMessageId = '',
        isFetchingMoreMessagesFromServer = false,
        participants = [],
        lastUpdatedAt = 0,
        messagesUpdated = false,
        completedPrechatCapture = false,
        metadata,
        appMakerLastRead = undefined,
    } = conversation;
    return {
        _id,
        unreadCount,
        messages,
        replyActions: {
            actions: [],
            message: {},
        },
        isDefault,
        lastUpdatedAt,
        rejectedMessages: {},
        completedUploads: [],
        failedMessages: [],
        messagesUpdated,
        hasMoreMessages,
        isFetchingMoreMessagesFromServer,
        lastReceivedMessageId,
        latestAppUserMessageTimestamp,
        participants,
        type,
        completedPrechatCapture,
        metadata,
        appMakerLastRead,
    };
}

export function addConversation(conversation, state) {
    const conversationState = formatConversation(conversation);

    if (state.default) {
        // remove default conversation and copy over the messages
        if (conversationState.messages.length === 0 && state.default.messages.length > 0) {
            conversationState.messages = state.default.messages;
        }
        state = _.omit(state, 'default');
    }

    return {
        ...state,
        [conversation._id]: conversationState,
    };
}

/**
 * Resolve conversation from server and current state of the conversation in reducer
 */
function resolveConversations(newConvo, currentConvo) {
    if (!currentConvo.messagesUpdated) {
        return formatConversation(newConvo);
    }

    return formatConversation({
        ...currentConvo,
        lastUpdatedAt: newConvo.lastUpdatedAt || 0,
        participants: newConvo.participants || [],
    });
}

/**	Used to initialize conversation list to reducer	*/
function addConversations(conversations, state = {}) {
    if (conversations.length === 0) {
        return state;
    }

    if (state.default) {
        // remove default conversation and copy over the messages
        if (
            (!conversations[0].messages || conversations[0].messages.length === 0) &&
            state.default.messages.length > 0
        ) {
            conversations[0].messages = state.default.messages;
        }

        state = _.omit(state, 'default');
    }

    return conversations.reduce((state, conversation) => {
        if (conversation && conversation._id) {
            return {
                ...state,
                [conversation._id]: state[conversation._id]
                    ? resolveConversations(conversation, state[conversation._id])
                    : formatConversation(conversation),
            };
        }

        return state;
    }, state);
}

const sortMessages = (messages) =>
    messages.sort((messageA, messageB) => {
        const messageADate = messageA.received || messageA._clientSent;
        const messageBDate = messageB.received || messageB._clientSent;

        return messageADate - messageBDate;
    });

const isCarousel = (message) => message.type === 'carousel' || message.type === 'list';

const manageGroupsBetweenMessages = (messages, previousMessageIndex, message) => {
    const previousMessage = previousMessageIndex >= 0 && messages[previousMessageIndex];

    if (previousMessage) {
        const previousMessageTs = new Date((previousMessage._clientSent || previousMessage.received) * 1000);
        const messageTs = new Date((message._clientSent || message.received) * 1000);
        const differentDate = !isSameDay(previousMessageTs, messageTs);

        if (!areFromSameAuthor(message, previousMessage) || differentDate) {
            previousMessage.lastInGroup = true;
            message.firstMessageOfDay = differentDate;
            message.firstInGroup = true;
            message.shouldShowAuthor = true;
        } else {
            if (!areFromSameAuthor(message, previousMessage)) {
                message.firstInGroup = true;
                message.shouldShowAuthor = true;
            } else {
                previousMessage.shouldShowAvatar = false;

                if (isCarousel(previousMessage) || isCarousel(message)) {
                    message.firstInGroup = true;
                } else {
                    previousMessage.lastInGroup = false;
                    previousMessage.shouldShowAvatar = false;
                }
            }

            message.firstMessageOfDay = false;
        }

        messages[previousMessageIndex] = previousMessage;
    } else {
        message.firstInGroup = true;
        message.shouldShowAuthor = true;
        message.firstMessageOfDay = true;
    }

    // these are always true
    message.lastInGroup = true;
    message.shouldShowAvatar = true;
};

export const extractReplyActions = ({ actions = [] } = {}) => {
    return actions.filter(({ type }) => type === 'reply' || type === 'locationRequest');
};

const isReplyMessageWithNoText = (message) => {
    const replyActions = extractReplyActions(message);

    const hasText = (message.text && message.text.trim()) || (message.mediaUrl && message.mediaUrl.trim());
    return replyActions.length > 0 && !hasText;
};

const addMessage = (messages, message) => {
    if (isReplyMessageWithNoText(message)) {
        // if the message contains reply actions and has no text,
        // don't add it to the messages
        return messages;
    }

    if (message._id) {
        const messageExists = messages.some((m) => m._id === message._id);

        if (messageExists) {
            // Duplicate faye event received, don't add the message again
            return messages;
        }
    }

    const messagesLength = messages.length;
    manageGroupsBetweenMessages(messages, messagesLength - 1, message);
    return sortMessages([...messages, message]);
};

const matchMessage = (message, queryProps) => Object.keys(queryProps).every((key) => message[key] === queryProps[key]);

const removeMessage = (messages, queryProps) => {
    const indexRemoved = messages.findIndex((message) => matchMessage(message, queryProps));
    const messagesAfterRemoval = [...messages.filter((message) => !matchMessage(message, queryProps))];

    if (indexRemoved >= 0) {
        const startIndex = indexRemoved === 0 ? indexRemoved : indexRemoved - 1;

        // manage groups for the previous message and all messages after to ensure correct avatar / border radius
        for (let i = startIndex; i < messagesAfterRemoval.length; i++) {
            manageGroupsBetweenMessages(messagesAfterRemoval, i - 1, messagesAfterRemoval[i]);
        }
    }

    return { messages: messagesAfterRemoval, indexRemoved };
};

const rejectMessage = (messages, query) => {
    let rejectedMessage;
    const existingMessage = messages.find((message) => matchMessage(message, query));

    if (!existingMessage) {
        return messages;
    }

    if (existingMessage._clientId) {
        rejectedMessage = {
            ...existingMessage,
            sendStatus: SEND_STATUS.FAILED,
            _clientId: existingMessage._clientId,
            lastInGroup: existingMessage.lastInGroup,
            firstInGroup: existingMessage.firstInGroup,
        };
    }

    const index = messages.indexOf(existingMessage);
    return [...messages.slice(0, index), rejectedMessage, ...messages.slice(index + 1)];
};

const addRejected = ({ rejectedMessages, queryProps, error }) => {
    if (!queryProps._id || !error) {
        return rejectedMessages;
    }

    return {
        ...rejectedMessages,
        [queryProps._id]: error,
    };
};

export const assignGroups = (messages) => {
    messages.forEach((message, index) => {
        manageGroupsBetweenMessages(messages, index - 1, message);
    });

    return messages;
};

const replaceMessage = (messages, query, newMessage) => {
    const existingMessage = messages.find((message) => matchMessage(message, query));

    if (!existingMessage) {
        return { messages };
    }

    if (existingMessage._clientId) {
        newMessage = {
            ...newMessage,
            _clientId: existingMessage._clientId,
            lastInGroup: existingMessage.lastInGroup,
            firstInGroup: existingMessage.firstInGroup,
        };
    }

    const existingMessageIndex = messages.indexOf(existingMessage);
    const sortedMessages = assignGroups(
        sortMessages([
            ...messages.slice(0, existingMessageIndex),
            newMessage,
            ...messages.slice(existingMessageIndex + 1),
        ]),
    );

    return {
        messages: sortedMessages,
        indexReplaced: sortedMessages.indexOf(newMessage),
    };
};

const cleanUpMessages = (messages, keepEmptyReplyActionMessages = false) => {
    const cleanedMessages = [];
    const messagesHash = {};
    const formsHash = {};

    // removes duplicate messages and empty messages
    messages.forEach((message) => {
        const messageText = (message.text && !!message.text.trim()) || (message.mediaUrl && !!message.mediaUrl.trim());

        let key = message._id
            ? message._id + message.role + message.mediaType
            : message._clientSent + message.role + message.mediaType;

        // if there is no messageId, message is not yet sent
        if (!message._id) {
            key = message._clientSent;
        }

        const isNonEmptyForm = message.type === MESSAGE_TYPES.FORM && message.fields.length > 0;
        if (isNonEmptyForm) {
            formsHash[message._id] = true;
        }
        const isFormResponseWithAssociatedForm =
            message.type === MESSAGE_TYPES.FORM_RESPONSE && !!formsHash[message.quotedMessageId];

        const hasReplyActionMessage = extractReplyActions(message).length;
        const hasContent =
            messageText ||
            (message.actions && message.actions.some(({ type }) => !GLOBAL_ACTION_TYPES.includes(type))) ||
            message.coordinates ||
            isNonEmptyForm ||
            isFormResponseWithAssociatedForm;

        if (!(key in messagesHash) && (hasContent || (keepEmptyReplyActionMessages && hasReplyActionMessage))) {
            messagesHash[key] = message;
            cleanedMessages.push(message);
        }
    });

    return cleanedMessages;
};

const getReplyActions = (messages, currentReplyActionMessage, conversation) => {
    let allMessages;
    if (currentReplyActionMessage && isReplyMessageWithNoText(currentReplyActionMessage)) {
        const messagesWithCurrentAction = [...messages, currentReplyActionMessage];
        allMessages = sortMessages(cleanUpMessages(messagesWithCurrentAction, true));
    } else {
        allMessages = sortMessages(cleanUpMessages(messages, true));
    }

    let replyActionMessage = allMessages[allMessages.length - 1];

    if (delegate.hasBeforeDisplay()) {
        const filteredMessages = dropRightWhile(
            allMessages,
            (message) => !delegate.beforeDisplay(message, conversation),
        );
        if (filteredMessages.length > 0) {
            replyActionMessage = filteredMessages[filteredMessages.length - 1];
        }
    }

    return {
        actions: extractReplyActions(replyActionMessage),
        message: replyActionMessage,
    };
};

const findLatestAppUserMessageTimestamp = (messages) => {
    const latestAppUserMessageIndex = _.findLastIndex(messages, { role: 'appUser' });
    const latestAppUserMessage = messages[latestAppUserMessageIndex];

    return (latestAppUserMessage && latestAppUserMessage.received) || 0;
};

export const addParticipant = (participants, participant) => {
    const currentParticipant = _.find(participants, (p) => p._id === participant._id);

    if (currentParticipant) {
        currentParticipant.unreadCount = 0;
        return participants;
    }

    return [...participants, { ...participant, unreadCount: 0 }];
};

export const removeParticipant = (participants, participant) => {
    return participants.filter((p) => p._id !== participant._id);
};

function setParticipantLastRead(participants, { appUserId, lastRead }) {
    const participantToUpdate = participants.find((participant) => participant.appUserId === appUserId);

    if (participantToUpdate) {
        const participant = { ...participantToUpdate, unreadCount: 0, lastRead };
        const updatedParticipants = replaceElement(participants, participant);

        if (updatedParticipants.length === participants.length) {
            return updatedParticipants;
        }

        return participants;
    }

    return participants;
}

export default function ConversationsReducer(state = { ...INITIAL_STATE }, action) {
    // actions that don't require conversationId
    switch (action.type) {
        case RESET:
        case ConversationActions.RESET_CONVERSATION:
            return {
                ...INITIAL_STATE,
            };
        case ConversationActions.SET_CONVERSATIONS: {
            return addConversations(action.conversations, state);
        }
        case ConversationActions.SET_CONVERSATION: {
            const { _id, unreadCount, hasMoreMessages, messagesUpdated, participants } = action.conversation;
            if (state[_id]) {
                return {
                    ...state,
                    [_id]: {
                        ...state[_id],
                        messagesUpdated: _.isBoolean(messagesUpdated) ? messagesUpdated : state[_id].messagesUpdated,
                        unreadCount: _.isNumber(unreadCount) ? unreadCount : state[_id].unreadCount,
                        hasMoreMessages: _.isBoolean(hasMoreMessages) ? hasMoreMessages : state[_id].hasMoreMessages,
                        participants: participants || state[_id].participants,
                    },
                };
            } else {
                return addConversation(action.conversation, state);
            }
        }
        case ConversationActions.REFRESH_CONVERSATIONS: {
            const { conversations, keepConvoWithFetchedMessages } = action;

            let newState = { ...state };

            const keptConversations = _.pick(state, (conversation) => conversation.messagesUpdated);
            newState =
                !keepConvoWithFetchedMessages || _.isEmpty(keptConversations)
                    ? { ...INITIAL_STATE }
                    : keptConversations;

            return addConversations(conversations, newState);
        }
    }

    // actions with conversation in store required, otherwise ignore since conversation may have not been fetched.
    const { conversationId } = action;
    const conversation = state[conversationId];

    if (!conversation) {
        return state;
    }

    switch (action.type) {
        case ConversationActions.SET_MESSAGES: {
            const { messages } = action;
            const newMessages = [...messages];
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    latestAppUserMessageTimestamp: findLatestAppUserMessageTimestamp(newMessages),
                    messages: assignGroups(sortMessages(cleanUpMessages(newMessages))),
                    messagesUpdated: true,
                    replyActions: getReplyActions(newMessages, [], conversation),
                },
            };
        }
        case ConversationActions.MERGE_MESSAGES: {
            const { messages } = action;
            const newMessages = assignGroups(sortMessages(cleanUpMessages([...messages, ...conversation.messages])));
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    latestAppUserMessageTimestamp: findLatestAppUserMessageTimestamp(newMessages),
                    messages: newMessages,
                    replyActions: getReplyActions(newMessages),
                },
            };
        }
        case ConversationActions.ADD_MESSAGES: {
            const { messages } = action;
            const newMessages = [...messages, ...conversation.messages];
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    messages: assignGroups(sortMessages(cleanUpMessages(newMessages))),
                },
            };
        }
        case ConversationActions.ADD_MESSAGE: {
            const { message } = action;
            const allMessages = addMessage(conversation.messages, message);
            const lastMessage = _.last(allMessages);

            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    lastUpdatedAt: (lastMessage && lastMessage.received) || conversation.lastUpdatedAt,
                    messages: allMessages,
                    replyActions: getReplyActions(
                        [...conversation.messages, message],
                        conversation.replyActions.message,
                        conversation,
                    ),
                },
            };
        }
        case ConversationActions.ADD_COMPLETED_UPLOAD:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    completedUploads: [...conversation.completedUploads, action.message],
                },
            };
        case ConversationActions.REMOVE_COMPLETED_UPLOAD:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    completedUploads: [
                        ...conversation.completedUploads.filter(
                            (completedUpload) => !matchMessage(completedUpload, action.queryProps),
                        ),
                    ],
                },
            };
        case ConversationActions.REJECT_MESSAGE:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    messages: assignGroups(sortMessages(rejectMessage(conversation.messages, action.queryProps))),
                    rejectedMessages: addRejected({
                        rejectedMessages: conversation.rejectedMessages,
                        queryProps: action.queryProps,
                        error: action.error,
                    }),
                },
            };
        case ConversationActions.REPLACE_MESSAGE: {
            const { message, queryProps } = action;
            const { messages, indexReplaced } = replaceMessage(conversation.messages, queryProps, message);
            const lastMessage = _.last(messages);

            const newConversationState = {
                ...conversation,
                lastUpdatedAt: (lastMessage && lastMessage.received) || conversation.lastUpdatedAt,
                messages,
            };
            if (indexReplaced === messages.length - 1) {
                newConversationState.replyActions = {
                    actions: extractReplyActions(lastMessage),
                    message: lastMessage,
                };
            }
            return {
                ...state,
                [conversationId]: newConversationState,
            };
        }
        case ConversationActions.REMOVE_MESSAGE: {
            const { queryProps } = action;

            const lastMessageIndex = conversation.messages.length - 1;
            const previousMessage = conversation.messages[conversation.messages.length - 2];
            const { messages, indexRemoved } = removeMessage(conversation.messages, queryProps);

            const newConversationState = {
                ...conversation,
                messages,
            };

            if (indexRemoved === lastMessageIndex) {
                newConversationState.replyActions = {
                    actions: !previousMessage ? [] : extractReplyActions(previousMessage),
                    message: previousMessage,
                };
            }

            return {
                ...state,
                [conversationId]: newConversationState,
            };
        }
        case ConversationActions.INCREMENT_UNREAD_COUNT:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    unreadCount: conversation.unreadCount + 1,
                },
            };
        case ConversationActions.RESET_UNREAD_COUNT:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    unreadCount: 0,
                },
            };
        case ConversationActions.SET_FETCHING_MORE_MESSAGES_FROM_SERVER: {
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    isFetchingMoreMessagesFromServer: action.value,
                },
            };
        }
        case ConversationActions.SET_LAST_RECEIVED_MESSAGE_ID: {
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    lastReceivedMessageId: action.value,
                },
            };
        }
        case ConversationActions.SET_COMPLETED_PRECHAT_CAPTURE:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    completedPrechatCapture: action.value,
                },
            };
        case ConversationActions.SET_COMPLETED_PRECHAT_CAPTURE_LINKING:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    completedPrechatCaptureLinking: action.value,
                },
            };
        case ConversationActions.SET_LATEST_APPUSER_MESSAGE_TIMESTAMP:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    latestAppUserMessageTimestamp: action.value,
                },
            };
        case ConversationActions.SET_APP_MAKER_LAST_READ:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    appMakerLastRead: action.appMakerLastRead,
                },
            };
        case ConversationActions.ADD_PARTICIPANT:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    participants: addParticipant(conversation.participants, action.participant),
                },
            };
        case ConversationActions.REMOVE_PARTICIPANT:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    participants: removeParticipant(conversation.participants, action.participant),
                },
            };
        case ConversationActions.SET_PARTICIPANT_LAST_READ:
            return {
                ...state,
                [conversationId]: {
                    ...conversation,
                    participants: setParticipantLastRead(conversation.participants, {
                        appUserId: action.appUserId,
                        lastRead: action.lastRead,
                    }),
                },
            };
        default:
            return state;
    }
}
